<template>
  <div class="input-group">
    <select
      :value="filterOperator"
      class="form-select"
      style="max-width: 120px"
      @change="onFilterOperatorChange($event.target.value)"
    >
      <option value="=">{{ $t('Equal to') }}</option>
      <option value="!=">{{ $t('Different from') }}</option>
      <option value="is_null">{{ $t('Is empty') }}</option>
      <option value="is_not_null">{{ $t('Is not empty') }}</option>
    </select>
    <multiselect
      :value="currentValue"
      :options="listOptions"
      :multiple="true"
      :close-on-select="false"
      :clear-on-select="true"
      :preserve-search="true"
      :preselect-first="false"
      :allow-empty="true"
      :searchable="true"
      :show-labels="true"
      :disabled="filterOperator === 'is_null' || filterOperator === 'is_not_null'"
      label="label"
      track-by="_id"
      :placeholder="'Select one or more options...'"
      @input="onFilterValueChange"
      :custom-label="(option) => option.label || option.name || String(option._id)"
    />
  </div>
</template>

<script>
import Multiselect from "vue-multiselect";
import "vue-multiselect/dist/vue-multiselect.min.css";
import { wencesApi } from "@/api/wencesApi";

export default {
  name: "FilterFieldList",
  components: {
    Multiselect,
  },
  props: {
    field: {
      type: Object,
      required: true,
    },
    value: {
      type: [String, Number, Object, Array],
      default: null,
    },
    fieldId: {
      type: String,
      default: null,
    },
    filterOperator: {
      type: String,
      default: "=",
    },
    lists: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      listOptions: [],
      isLoading: false,
    };
  },
  computed: {
    currentValue() {
      if (this.value !== null && this.value !== undefined && this.value !== "") {
        if (Array.isArray(this.value)) {
          return this.value.map((val) => this.normalizeValue(val));
        }
        return this.normalizeValue(this.value);
      }
      return null;
    },
    computedFieldId() {
      return this.fieldId || `filter-list-${this.field.key}`;
    },
  },
  async mounted() {
    await this.loadListOptions();
  },
  watch: {
    lists: {
      handler() {
        this.loadListOptions();
      },
      deep: true,
    },
    "field.listType"() {
      this.loadListOptions();
    },
  },
  methods: {
    async loadListOptions() {
      if (!this.field.listType) {
        this.listOptions = [];
        return;
      }
      const existingList = this.lists.find((l) => l.type === this.field.listType);
      if (existingList && existingList.values) {
        this.listOptions = existingList.values;
        return;
      }
      if (this.isLoading) return;
      this.isLoading = true;
      try {
        const response = await wencesApi.get(
          `/api/gotrading/lists/type/${this.field.listType}`
        );
        if (response.data && response.data.values) {
          this.listOptions = response.data.values;
        } else {
          this.listOptions = [];
        }
      } catch (error) {
        console.error(
          `Error loading list options for ${this.field.listType}:`,
          error
        );
        this.listOptions = [];
      } finally {
        this.isLoading = false;
      }
    },
    normalizeValue(value) {
      if (typeof value === "object" && value !== null && value._id) {
        return value;
      }
      const valueId = typeof value === "object" ? value._id || value.id : value;
      if (valueId && this.listOptions.length > 0) {
        const option = this.listOptions.find((opt) => {
          const optId = opt._id || opt.id;
          return optId && optId.toString() === String(valueId);
        });
        if (option) {
          return option;
        }
      }
      return {
        _id: valueId || value,
        label: valueId || value,
      };
    },
    onFilterOperatorChange(operator) {
      this.$emit("filter-operator-change", operator);
    },
    onFilterValueChange(newValue) {
      let valueToEmit = newValue;
      if (Array.isArray(newValue)) {
        valueToEmit = newValue.map((item) =>
          typeof item === "object" && item._id ? item._id : item
        );
      } else if (newValue && typeof newValue === "object" && newValue._id) {
        valueToEmit = newValue._id;
      }
      this.$emit("input", valueToEmit);
    },
  },
};
</script>

<style scoped>
.input-group {
  width: 100%;
}

:deep(.multiselect-container) {
  position: relative;
  z-index: 1;
}

:deep(.multiselect.is-active) {
  z-index: 99999;
}

:deep(.multiselect__content-wrapper) {
  top: 100% !important;
  bottom: auto !important;
  transform: none !important;
  z-index: 99999 !important;
  position: absolute !important;
}

:deep(.multiselect__content) {
  max-height: 200px;
  overflow-y: auto;
}
</style>

